import React from 'react';
import { MessageCircle, Phone, ExternalLink, Mic, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * BookingAssistant Component
 * 
 * Displays booking options for patients:
 * - SMS Booking (Active) - For discreet booking via text
 * - Voice Assistant WebRTC (Active) - For voice-based booking via browser
 * - WhatsApp Chatbot (Coming Soon) - Future feature
 */
const BookingAssistant = () => {
  const { t, language } = useLanguage();
  
  // Vonage SMS number for booking (to be updated with real Vonage virtual number)
  const SMS_BOOKING_NUMBER = "+352621411475";
  
  const openSMSBooking = () => {
    // Open SMS app with pre-filled message
    const message = encodeURIComponent("RDV");
    const url = `sms:${SMS_BOOKING_NUMBER}?body=${message}`;
    window.open(url, '_self');
  };

  const openVoiceAssistant = () => {
    // Trigger the floating voice assistant button click
    const voiceButton = document.querySelector('[data-testid="voice-assistant-button"]');
    if (voiceButton) {
      voiceButton.click();
    } else {
      // Fallback: scroll to bottom right where the button is and alert user
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
      alert(language === 'fr' 
        ? 'Cliquez sur le bouton bleu en bas à droite pour parler à l\'assistant vocal.' 
        : 'Click the blue button at the bottom right to talk to the voice assistant.');
    }
  };
  
  const openWhatsApp = () => {
    // Open WhatsApp with pre-filled message for booking
    const phoneNumber = "352621411475"; // Luxembourg number without +
    const message = encodeURIComponent("Bonjour, je souhaite prendre un rendez-vous médical.");
    const url = `https://wa.me/${phoneNumber}?text=${message}`;
    window.open(url, '_blank');
  };

  const translations = {
    fr: {
      title: "Assistant de Prise de RDV",
      subtitle: "Choisissez votre méthode de réservation préférée",
      sms_title: "📱 Option Discrète",
      sms_subtitle: "Réservation par SMS",
      sms_description: "Prenez rendez-vous par SMS, idéal si vous êtes au travail ou dans un lieu public. Envoyez simplement 'RDV Dr Nom Date Heure'.",
      sms_button: "Envoyer un SMS",
      sms_features: ["Réponse automatique", "Disponible 24h/24", "100% discret"],
      sms_number: SMS_BOOKING_NUMBER,
      whatsapp_title: "💬 Option Discrète",
      whatsapp_subtitle: "Réservation WhatsApp",
      whatsapp_description: "Prenez rendez-vous via WhatsApp, idéal si vous êtes au travail ou dans un lieu public.",
      whatsapp_button: "Ouvrir WhatsApp",
      whatsapp_features: ["Réponse instantanée", "Disponible 24h/24", "Historique sauvegardé"],
      voice_title: "🎙️ Option Vocale",
      voice_subtitle: "Assistant IA Vapi",
      voice_description: "Parlez directement à notre assistant IA depuis votre navigateur pour prendre votre rendez-vous.",
      voice_button: "Parler à l'assistant",
      voice_features: ["Conversation naturelle", "Multi-langues", "Rappels automatiques"],
      coming_soon: "Bientôt",
      active: "Actif",
      cost_sms: "~0.07€/SMS",
      cost_voice: "Gratuit",
      cost_whatsapp: "Gratuit"
    },
    en: {
      title: "Appointment Booking Assistant",
      subtitle: "Choose your preferred booking method",
      sms_title: "📱 Discreet Option",
      sms_subtitle: "SMS Booking",
      sms_description: "Book appointments via SMS, ideal if you're at work or in a public place. Just send 'RDV Dr Name Date Time'.",
      sms_button: "Send SMS",
      sms_features: ["Auto-response", "Available 24/7", "100% discreet"],
      sms_number: SMS_BOOKING_NUMBER,
      whatsapp_title: "💬 Discreet Option",
      whatsapp_subtitle: "WhatsApp Booking",
      whatsapp_description: "Book appointments via WhatsApp, ideal if you're at work or in a public place.",
      whatsapp_button: "Open WhatsApp",
      whatsapp_features: ["Instant response", "Available 24/7", "Conversation saved"],
      voice_title: "🎙️ Voice Option",
      voice_subtitle: "Vapi AI Assistant",
      voice_description: "Speak directly with our AI assistant from your browser to book your appointment.",
      voice_button: "Talk to assistant",
      voice_features: ["Natural conversation", "Multi-language", "Automatic reminders"],
      coming_soon: "Soon",
      active: "Active",
      cost_sms: "~€0.07/SMS",
      cost_voice: "Free",
      cost_whatsapp: "Free"
    },
    de: {
      title: "Terminbuchungs-Assistent",
      subtitle: "Wählen Sie Ihre bevorzugte Buchungsmethode",
      sms_title: "📱 Diskrete Option",
      sms_subtitle: "SMS-Buchung",
      sms_description: "Buchen Sie Termine per SMS, ideal wenn Sie bei der Arbeit oder an einem öffentlichen Ort sind.",
      sms_button: "SMS senden",
      sms_features: ["Auto-Antwort", "24/7 verfügbar", "100% diskret"],
      sms_number: SMS_BOOKING_NUMBER,
      whatsapp_title: "💬 Diskrete Option",
      whatsapp_subtitle: "WhatsApp Buchung",
      whatsapp_description: "Buchen Sie Termine per WhatsApp, ideal wenn Sie bei der Arbeit sind.",
      whatsapp_button: "WhatsApp öffnen",
      whatsapp_features: ["Sofortige Antwort", "24/7 verfügbar", "Gesprächsverlauf"],
      voice_title: "🎙️ Sprach-Option",
      voice_subtitle: "Vapi KI-Assistent",
      voice_description: "Sprechen Sie direkt mit unserem KI-Assistenten, um Ihren Termin zu buchen.",
      voice_button: "Mit Assistent sprechen",
      voice_features: ["Natürliches Gespräch", "Mehrsprachig", "Automatische Erinnerungen"],
      coming_soon: "Bald",
      active: "Aktiv",
      cost_sms: "~0,07€/SMS",
      cost_voice: "Kostenlos",
      cost_whatsapp: "Kostenlos"
    },
    ar: {
      title: "مساعد حجز المواعيد",
      subtitle: "اختر طريقة الحجز المفضلة لديك",
      sms_title: "📱 خيار سري",
      sms_subtitle: "الحجز عبر SMS",
      sms_description: "احجز المواعيد عبر الرسائل النصية، مثالي إذا كنت في العمل أو في مكان عام.",
      sms_button: "إرسال رسالة",
      sms_features: ["رد تلقائي", "متاح 24/7", "سري 100%"],
      sms_number: SMS_BOOKING_NUMBER,
      whatsapp_title: "💬 خيار سري",
      whatsapp_subtitle: "حجز واتساب",
      whatsapp_description: "احجز المواعيد عبر واتساب، مثالي إذا كنت في العمل.",
      whatsapp_button: "فتح واتساب",
      whatsapp_features: ["رد فوري", "متاح 24/7", "سجل المحادثات"],
      voice_title: "🎙️ خيار صوتي",
      voice_subtitle: "مساعد Vapi الذكي",
      voice_description: "تحدث مباشرة مع مساعدنا الذكي لحجز موعدك.",
      voice_button: "التحدث مع المساعد",
      voice_features: ["محادثة طبيعية", "متعدد اللغات", "تذكيرات تلقائية"],
      coming_soon: "قريباً",
      active: "نشط",
      cost_sms: "~0.07€/رسالة",
      cost_voice: "مجاني",
      cost_whatsapp: "مجاني"
    },
    es: {
      title: "Asistente de Reserva de Citas",
      subtitle: "Elija su método de reserva preferido",
      whatsapp_title: "💬 Opción Discreta",
      whatsapp_subtitle: "Reserva WhatsApp",
      whatsapp_description: "Reserve citas por WhatsApp, ideal si está en el trabajo.",
      whatsapp_button: "Abrir WhatsApp",
      whatsapp_features: ["Respuesta instantánea", "Disponible 24/7", "Historial guardado"],
      voice_title: "🎙️ Opción de Voz",
      voice_subtitle: "Asistente IA Vapi",
      voice_description: "Hable directamente con nuestro asistente IA desde su navegador.",
      voice_button: "Hablar con asistente",
      voice_features: ["Conversación natural", "Multi-idioma", "Recordatorios automáticos"],
      coming_soon: "Pronto",
      active: "Activo",
      cost_whatsapp: "Gratis",
      cost_voice: "Gratis"
    },
    lb: {
      title: "Rendezvous Buchungs Assistent",
      subtitle: "Wielt Är bevorzuugt Buchungsmethode",
      whatsapp_title: "💬 Diskret Optioun",
      whatsapp_subtitle: "WhatsApp Buchung",
      whatsapp_description: "Maacht Rendezvousen iwwer WhatsApp, ideal wann Dir op der Aarbecht sidd.",
      whatsapp_button: "WhatsApp opmaachen",
      whatsapp_features: ["Direkt Äntwert", "24/7 disponibel", "Gespréich Geschicht"],
      voice_title: "🎙️ Stëmm Optioun",
      voice_subtitle: "Vapi KI Assistent",
      voice_description: "Schwätzt direkt mat eisem KI Assistent iwwer Äre Browser fir Ären Rendezvous ze maachen.",
      voice_button: "Mat Assistent schwätzen",
      voice_features: ["Natierlech Gespréich", "Multi-Sproochen", "Automatesch Rappellen"],
      coming_soon: "Geschwënn",
      active: "Aktiv",
      cost_whatsapp: "Gratis",
      cost_voice: "Gratis"
    }
  };

  const text = translations[language] || translations.fr;

  return (
    <div className="max-w-4xl mx-auto p-4" data-testid="booking-assistant">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-slate-900 mb-2">{text.title}</h2>
        <p className="text-slate-600">{text.subtitle}</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* WhatsApp Option - Coming Soon */}
        <Card className="border-2 border-gray-300 bg-gray-50/50 hover:shadow-lg transition-shadow relative overflow-hidden">
          <div className="absolute top-0 right-0 bg-amber-500 text-white px-3 py-1 text-xs font-bold transform rotate-0 rounded-bl-lg">
            À venir
          </div>
          <CardHeader>
            <div className="flex items-center justify-between">
              <Badge className="bg-amber-500 text-white">{text.coming_soon}</Badge>
              <MessageCircle className="w-8 h-8 text-gray-400" />
            </div>
            <CardTitle className="text-xl mt-4 text-gray-600">{text.whatsapp_title}</CardTitle>
            <CardDescription className="text-base font-medium text-gray-500">
              {text.whatsapp_subtitle}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-gray-500 mb-4">{text.whatsapp_description}</p>
            
            <ul className="space-y-2 mb-6">
              {text.whatsapp_features.map((feature, index) => (
                <li key={index} className="flex items-center gap-2 text-sm text-gray-500">
                  <span className="text-gray-400">✓</span>
                  {feature}
                </li>
              ))}
            </ul>
            
            <Button 
              disabled
              className="w-full bg-gray-400 cursor-not-allowed gap-2"
              data-testid="whatsapp-button"
            >
              <MessageCircle className="w-5 h-5" />
              {text.whatsapp_button}
              <ExternalLink className="w-4 h-4" />
            </Button>
          </CardContent>
        </Card>

        {/* Voice Option - WebRTC Active */}
        <Card className="border-2 border-blue-500 bg-blue-50/50 hover:shadow-lg transition-shadow">
          <CardHeader>
            <div className="flex items-center justify-between">
              <Badge className="bg-blue-500 text-white">{text.active}</Badge>
              <Mic className="w-8 h-8 text-blue-600" />
            </div>
            <CardTitle className="text-xl mt-4">{text.voice_title}</CardTitle>
            <CardDescription className="text-base font-medium text-blue-700">
              {text.voice_subtitle}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-slate-600 mb-4">{text.voice_description}</p>
            
            <ul className="space-y-2 mb-6">
              {text.voice_features.map((feature, index) => (
                <li key={index} className="flex items-center gap-2 text-sm text-slate-700">
                  <span className="text-blue-500">✓</span>
                  {feature}
                </li>
              ))}
            </ul>
            
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-slate-500">
                🌐 {language === 'fr' ? 'Via votre navigateur' : 'Via your browser'}
              </span>
            </div>
            
            <Button 
              onClick={openVoiceAssistant}
              className="w-full bg-blue-600 hover:bg-blue-700 gap-2"
              data-testid="voice-button"
            >
              <Mic className="w-5 h-5" />
              {text.voice_button}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Info Box */}
      <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <p className="text-sm text-blue-800 text-center">
          💡 <strong>Astuce :</strong> L'option WhatsApp est idéale pour prendre RDV discrètement depuis votre lieu de travail. 
          L'assistant vocal WebRTC permet de parler directement avec notre IA depuis votre navigateur.
        </p>
      </div>
    </div>
  );
};

export default BookingAssistant;
